import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatCount } from '@/utils/number';
import { submitPcmpRequests } from '../api/pcmpRequests.api';
import type { PcmpRequestSubmitPayload, PcmpRequestSubmitResult } from '../types/pcmpRequest.types';
import { pcmpActionCallbacks } from '../utils/pcmpActionCallbacks';
import { usePcmpFileUploader } from './usePcmpFileUploader';

/** A Payment Return request's per-proposal amount and slip file, before upload. */
export interface PcmpRequestReturnInput {
  proposalId: number;
  amount?: number | undefined;
  slip: File | null;
}

export interface PcmpRequestSubmitInput {
  payload: PcmpRequestSubmitPayload;
  /** Payment Return only (LDS-765); each slip is uploaded before the request is sent. */
  returns?: PcmpRequestReturnInput[] | undefined;
}

/** Raise one request category on the picked proposals. */
export function useSubmitPcmpRequests(onSuccess: (result: PcmpRequestSubmitResult) => void) {
  const queryClient = useQueryClient();
  const upload = usePcmpFileUploader();

  return useMutation({
    mutationFn: async ({ payload, returns }: PcmpRequestSubmitInput) => {
      if (!returns) return submitPcmpRequests(payload);
      const items = [];
      // One at a time: an upload failure stops before anything is raised.
      for (const { proposalId, amount, slip } of returns) {
        items.push({ proposalId, amount, slipKey: slip ? await upload(slip) : undefined });
      }
      return submitPcmpRequests({ ...payload, returns: items });
    },
    ...pcmpActionCallbacks<PcmpRequestSubmitResult>(
      queryClient,
      (result) => `${formatCount(result.requested, 'request')} raised: ${result.category.name}`,
      onSuccess,
    ),
  });
}
